import { RouterOutput, ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation, useQueryClient } = ReactQueryV5;
import CustomModal from "components/lib/customModal";
import { Typography } from "components/typography";
import { useDemo } from "contexts/DemoContext";
import { PropsWithChildren, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useMindlerApi } from "../../../../../api/apiv2";
import { PhoneField } from "../../../../phone-field";

export const PatientDataModal = ({
  open,
  onClose,
  patient,
}: PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  patient: Exclude<RouterOutput["patients"]["getByUserId"], null>;
}>) => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState(patient?.phoneNumber ?? "");
  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);
  const api = useMindlerApi();
  const queryClient = useQueryClient();
  const { mutateAsync: updatePhoneNumber } = useMutation(
    api.users.updateUserPhoneByUserId.mutationOptions(),
  );

  const demo = useDemo();
  const updatePatientPhoneNumber = async () => {
    try {
      await updatePhoneNumber({
        userId: patient.userId,
        role: "Patient",
        phone: phoneNumber,
      });
      onClose();
      setConfirmModalIsOpen(false);
      await queryClient.invalidateQueries(
        api.patients.getByUserId.queryFilter({
          patientUserId: patient.userId,
        }),
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <CustomModal.Wrapper
        onClose={onClose}
        open={confirmModalIsOpen}
        titleText={t("CONFIRM_PHONE_NUMBER")}
        size="medium"
      >
        <p>
          <Trans
            i18nKey="CONFIRM_PATIENT_NUMBER_TEXT"
            values={{
              phone: phoneNumber,
            }}
            components={{
              strong: <strong />,
            }}
          />
        </p>

        <div style={{ marginBottom: "1rem" }} />

        <CustomModal.ButtonRow>
          <CustomModal.Button
            disabled={false}
            onClick={() => setConfirmModalIsOpen(false)}
            text={t("CANCEL")}
            variant="secondary"
          />
          <CustomModal.Button
            onClick={() => updatePatientPhoneNumber()}
            text={"Confirm"}
            variant="primary"
          />
        </CustomModal.ButtonRow>
      </CustomModal.Wrapper>

      <CustomModal.Wrapper
        onClose={onClose}
        open={open}
        titleText={`${t("EDIT_DETAILS")}: ${patient?.firstName} ${
          patient?.lastName
        }`}
        size="medium"
      >
        <CustomModal.Row>
          <CustomModal.Column>
            <Typography
              variant="body-x-small"
              color="GRAY_DARK"
              style={{ display: "block", marginBottom: "0.5rem" }}
            >
              {t("USER")}
            </Typography>
            {patient?.firstName}
          </CustomModal.Column>
          <CustomModal.Column>
            <Typography
              variant="body-x-small"
              color="GRAY_DARK"
              style={{ display: "block", marginBottom: "0.5rem" }}
            >
              {t("USER_ID")}
            </Typography>
            {patient?.userId}
          </CustomModal.Column>
          <CustomModal.Column>
            <Typography
              variant="body-x-small"
              color="GRAY_DARK"
              style={{ display: "block", marginBottom: "0.5rem" }}
            >
              {t("EMAIL")}
            </Typography>
            {patient?.email}
          </CustomModal.Column>
          <CustomModal.Column>
            <PhoneField
              value={phoneNumber}
              sensitive={demo}
              label={`${t("PHONE_NUMBER")}*`}
              onChange={({ value }) => setPhoneNumber(value)}
            />
          </CustomModal.Column>
        </CustomModal.Row>

        <CustomModal.ButtonRow>
          <CustomModal.Button
            disabled={false}
            onClick={onClose}
            text={t("CANCEL")}
            variant="secondary"
          />
          <CustomModal.Button
            onClick={() => setConfirmModalIsOpen(true)}
            text={t("UPDATE")}
            variant="primary"
          />
        </CustomModal.ButtonRow>
      </CustomModal.Wrapper>
    </>
  );
};
